// Notes SC6a R5 / SC6c D3 — dictation into the rich clinical field (decisions 25.6, 27, 28.10).
//  • Anchored insertion: the text goes where the caret was when the mic started (mapped through every later change);
//    if the user moves the caret in the editor while recording, dictation continues from there.
//  • Interim speech is a DECORATION only (never in the doc, never saved, never in the history).
//  • Each FINAL segment is its own history step (the history group is closed before each insert), so after recording
//    Undo removes one sentence at a time. Every insert is tagged as a RUN of this session (cleaned up on Stop).
//  • SC6c (decision 28.10): natural dictation — the AI pass on Stop punctuates, makes the lists and the spoken section
//    headings. The ONLY spoken command left is "stop recording" (EN + HE), matched as the WHOLE final segment; every
//    other old command phrase ("new line", "heading", "undo that"…) is now plain words.
//  • With the clean-up switched off (decision 28.5), the old inline punctuation words still apply ("comma" → ",").

// The one spoken command — the whole final segment (case-insensitive, trailing punctuation ignored).
export const DICT_COMMANDS = [
  { k: 'stop', phrases: ['stop recording', 'הפסק הקלטה'] },
];
// Inline punctuation (the pre-SC6c rule) — ONLY when the clean-up is off: the word anywhere in the segment becomes the mark.
const PUNCT = [
  { phrases: ['period', 'full stop', 'נקודה'], insert: '.' },
  { phrases: ['comma', 'פסיק'], insert: ',' },
  { phrases: ['question mark', 'סימן שאלה'], insert: '?' },
  { phrases: ['colon', 'נקודתיים'], insert: ':' },
  { phrases: ['dash', 'מקף'], insert: ' - ' },
];
const _esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export function matchCommand(seg){
  const s = String(seg || '').trim().replace(/[.,!?؟;:׃]+$/u, '').trim().toLowerCase();
  if (!s) return null;
  for (const c of DICT_COMMANDS) if (c.phrases.includes(s)) return c.k;
  return null;
}
export function applyPunctuation(seg){
  let r = String(seg || '');
  for (const p of PUNCT) for (const ph of p.phrases) r = r.replace(new RegExp('(^|\\s)' + _esc(ph) + '(?=\\s|$)', 'gi'), '$1' + p.insert);
  return r.replace(/\s+([.,?:])/g, '$1');
}

// SC6c D10 (decision 33): continuous recognition only on DESKTOP Chrome, detected conservatively — anything uncertain
// (Android, iOS, iPadOS "desktop" mode, Edge, Opera, other Chromium brands' UAs we can't tell apart, desktop Linux where
// Android's "desktop site" mode looks the same) stays continuous=false. Android Chrome's continuous mode duplicates /
// garbles results and iOS's grows a single result (Chromium 40324711, 40948113; WebKit reports).
export function dictContinuousOK(nav = navigator){
  try {
    const ua = String(nav.userAgent || '');
    if (!/\bChrome\/\d+/.test(ua)) return false;
    if (/\b(Edg|EdgA|EdgiOS|OPR|OPiOS|CriOS|FxiOS|SamsungBrowser|YaBrowser|Vivaldi)\//.test(ua)) return false;
    if (/Android|Mobile|iPhone|iPad|iPod/.test(ua)) return false;
    if (!/\bCrOS\b/.test(ua) && /X11|Linux/.test(ua)) return false; // ChromeOS says "X11; CrOS" — allowed; plain Linux isn't
    if (!/Windows NT|Macintosh|CrOS/.test(ua)) return false;
    if (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1) return false; // an iPad asking for the desktop site
    const d = nav.userAgentData;
    if (d) { if (d.mobile) return false; if (d.platform && !/^(Windows|macOS|Chrome OS)$/.test(d.platform)) return false; }
    return true;
  } catch (_) { return false; }
}
// Decision 33.3: the same final segment again within ~1 s (case, punctuation and spacing ignored) is an engine
// duplicate, not speech.
export const DICT_DUP_MS = 1000;
export const dictNorm = (s) => String(s || '').toLowerCase().replace(/[\p{P}\p{S}]/gu, ' ').replace(/\s+/g, ' ').trim();

// One dictation session into a RichField. → { interim(text), final(text) → 'stop' | undefined, sid, end() }
// opts.punct: apply the inline punctuation words (the clean-up is off for this session).
export function startDictation(field, opts = {}){
  const ed = field.editor; if (!ed) return null;
  const api = window.HUD_NOTES_RICH, key = api.interimKey;
  let anchor = ed.state.selection.head;
  let lastNorm = '', lastAt = -Infinity; // decision 33.3: the previous final segment (normalised) and when it came
  // SC6c: this session's id — every final insert is tagged as a RUN of it (cleaned up on Stop, dictation-pass.js)
  const sid = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  // the user moves the caret in the editor while recording → dictation continues from there
  const onTr = ({ transaction }) => {
    if (transaction.getMeta('hudDict')) return;
    if (transaction.docChanged) anchor = transaction.mapping.map(anchor);
    if (transaction.selectionSet && ed.isFocused) anchor = ed.state.selection.head;
  };
  ed.on('transaction', onTr);
  const clampA = () => Math.max(1, Math.min(anchor, Math.max(1, ed.state.doc.content.size - 1)));
  const needSpace = (pos) => { const $p = ed.state.doc.resolve(pos); const before = $p.parent.textBetween(0, $p.parentOffset, undefined, ' '); return before.length > 0 && !/\s$/.test(before); };
  const setInterim = (text) => {
    const pos = clampA();
    const t = text ? ((needSpace(pos) ? ' ' : '') + text) : '';
    ed.view.dispatch(ed.state.tr.setMeta(key, { pos: text ? pos : null, text: t }).setMeta('addToHistory', false).setMeta('hudDict', true));
  };
  // A final segment: ONE transaction, its own history step (closeHistory), the interim cleared.
  const step = (fn, run) => {
    const tr = ed.state.tr; fn(tr);
    tr.setMeta(key, { pos: null, text: '' }).setMeta('hudDict', true);
    if (run && api.runKey) tr.setMeta(api.runKey, { add: run }); // SC6c: the same transaction → still one undo step per sentence
    ed.view.dispatch(api.closeHistory(tr));
  };
  return {
    interim(text){ setInterim(String(text || '').trim()); },
    final(seg){
      const nrm = dictNorm(seg), now = performance.now();
      if (nrm && nrm === lastNorm && now - lastAt < DICT_DUP_MS) { setInterim(''); return 'dup'; } // decision 33.3
      if (nrm) { lastNorm = nrm; lastAt = now; }
      if (matchCommand(seg) === 'stop') { setInterim(''); return 'stop'; }
      const text = (opts.punct ? applyPunctuation(seg) : String(seg || '')).trim(); if (!text) { setInterim(''); return; }
      const pos = clampA(); const ins = (needSpace(pos) && !/^[.,?:]/.test(text) ? ' ' : '') + text;
      step(tr => tr.insertText(ins, pos), { from: pos, to: pos + ins.length, id: sid });
      anchor = pos + ins.length;
    },
    sid,
    end(){ try { setInterim(''); } catch (_) {} ed.off('transaction', onTr); },
  };
}
